'use client'

import { useEffect, useState } from 'react'
import { isNativePlatform } from '@/lib/utils/platform'
import { IOS_APP_STORE_URL, PLAY_STORE_URL } from '@/lib/config/app-install'

/**
 * The store URL to send the user to when the native app is below the minimum
 * version the server asks for (`/api/app-version`), or null. Checked on mount
 * and every time the app comes back to the foreground. On the web, and on
 * native without an answer from the server, always null. Shared by the
 * classic and the Encuentro update modals.
 */
export function useRequiredAppUpdate(): string | null {
  const [showModal, setShowModal] = useState(false)
  const [storeUrl, setStoreUrl] = useState<string>('')

  useEffect(() => {
    if (!isNativePlatform()) return

    let cancelled = false
    let removeAppStateListener: (() => void) | undefined

    async function checkVersion() {
      try {
        const { App } = await import('@capacitor/app')
        const { getPlatform } = await import('@/lib/utils/platform')

        const appInfo = await App.getInfo()
        const currentVersion = appInfo.version // e.g. "1.0.0"

        // Fetch minimum required version from server
        const res = await fetch('/api/app-version', { cache: 'no-store' })
        if (!res.ok) return

        const data = await res.json()
        const minimumVersion = data.minimumVersion as string

        if (!minimumVersion) return

        // Simple semver comparison
        if (isVersionBelow(currentVersion, minimumVersion)) {
          const platform = getPlatform()
          // Read store URLs from the central app-install config so any future
          // change (App Store ID, locale, package rename) flows here automatically.
          // The previous hardcoded values had drifted: the iOS URL was a
          // placeholder (`id0000000000`) that sent every Apple user prompted
          // to update straight to a non-existent listing.
          setStoreUrl(platform === 'ios' ? IOS_APP_STORE_URL : PLAY_STORE_URL)
          setShowModal(true)
        }
      } catch {
        // Version check is non-critical — never break the app
      }
    }

    void checkVersion()

    void import('@capacitor/app').then(({ App }) => {
      if (cancelled) return
      void App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) void checkVersion()
      }).then((handle) => {
        if (cancelled) {
          void handle.remove()
          return
        }
        removeAppStateListener = () => {
          void handle.remove()
        }
      })
    })

    return () => {
      cancelled = true
      removeAppStateListener?.()
    }
  }, [])

  return showModal ? storeUrl : null
}

/**
 * Compare two semver strings. Returns true if `current` is below `minimum`.
 * Handles "1.0.0" vs "1.0.1", "1.0" vs "1.0.0", etc.
 */
function isVersionBelow(current: string, minimum: string): boolean {
  const currentParts = current.split('.').map(Number)
  const minimumParts = minimum.split('.').map(Number)

  for (let i = 0; i < Math.max(currentParts.length, minimumParts.length); i++) {
    const curr = currentParts[i] || 0
    const min = minimumParts[i] || 0
    if (curr < min) return true
    if (curr > min) return false
  }

  return false // Equal versions
}
